import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { loadAssignableOwners } from "@/components/ProcessOwnerPicker";
import { WhatHappensNextPanel } from "@/components/WhatHappensNextPanel";
import { useSlotPicker } from "@/hooks/useSlotPicker";
import { SlotPickerView } from "@/components/SlotPickerView";
import { ArrowRight, Upload, Plus, Trash2, Bell, Loader2 } from "lucide-react";

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// UX-only mirror of process_workflow._applies — the SERVER re-validates
// every field/document/branch independently on submit.
const applies = (when, values) => (when || []).every((w) => String(values[w.field] ?? "") === String(w.value));

/**
 * A direct stage move onto a registry-governed Case's next stage is answered
 * `409 outcome_required` with the ONE pending task that moves it. The Case
 * stage rail already opens that task's form; this is the same decision for
 * any other caller (the Pipelines board drag): returns what to open —
 * {caseId, taskId, contactId, message} — or null for every other response,
 * which the caller keeps handling exactly as before. Nothing moves here;
 * only a server-accepted outcome moves the Case (and so the card).
 */
export function outcomeRequiredTarget(err, opp) {
  const d = err?.response?.data?.detail;
  if (err?.response?.status !== 409 || !d || typeof d !== "object") return null;
  if (d.blocker_type !== "outcome_required" || !d.task_id) return null;
  const caseId = opp?.case_id || d.case_id;
  if (!caseId) return null;
  return { caseId, taskId: d.task_id, contactId: opp?.contact_id || null, message: d.message || "" };
}

// UX mirror of process_workflow.normalise_doc_ids/normalise_draft: a draft saved
// by the older single-document form may hold a bare id (or nothing); always
// present a list. Anything not understood is kept verbatim (never dropped).
export const normaliseDraftDocuments = (documents) => {
  if (!documents || typeof documents !== "object" || Array.isArray(documents)) return {};
  const out = {};
  Object.entries(documents).forEach(([k, v]) => {
    if (typeof v === "string") out[k] = v ? [v] : [];
    else if (Array.isArray(v)) out[k] = [...new Set(v.filter((x) => typeof x === "string" && x))];
    else out[k] = v;
  });
  return out;
};

const USABLE_DOC_STATUSES = ["uploaded", "verified"];

const Section = ({ title, children }) => (
  <div className="space-y-2 rounded-md border p-3">
    <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">{title}</div>
    {children}
  </div>
);

/**
 * THE shared Process workflow outcome form (Workstream B / B2) — one
 * component for every governed task of every product, rendered entirely
 * from the server registry's schema (GET /cases/{id}/workflow).
 *
 * Mandatory-but-dismissible: closing, navigating away or reloading never
 * completes the task or moves the Case (no beforeunload trap, the dialog is
 * always closable); the draft is autosaved to the task and restored when
 * the SAME pending task is reopened. Submission carries an idempotency key
 * generated once per opening, so a double-click or a retried request can't
 * complete twice or advance the stage twice. Only a successful, server-
 * validated submission completes the task.
 */
export function WorkflowOutcomeDialog({ caseId, taskId, open, onOpenChange, onSubmitted, contactId }) {
  const [view, setView] = useState(null);
  const [values, setValues] = useState({});
  const [docs, setDocs] = useState({});
  const [notes, setNotes] = useState("");
  const [extraDocs, setExtraDocs] = useState([]);
  const [extraTasks, setExtraTasks] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [customerDocs, setCustomerDocs] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [draftState, setDraftState] = useState("");
  const [panel, setPanel] = useState(null); // skip | handoff | share | remind | null
  const [aux, setAux] = useState({});
  const [owners, setOwners] = useState([]);
  const [uploadCategory, setUploadCategory] = useState({}); // requirement key -> chosen category
  const idemKey = useRef(newKey());
  const hydrated = useRef(false);
  const saveTimer = useRef(null);
  const inFlight = useRef(false); // synchronous double-click guard

  const task = useMemo(() => (view?.tasks || []).find((t) => t.task_id === taskId), [view, taskId]);
  const schema = task?.schema;
  // R5: the server's `actionable` (open AND in the Case's current stage, or
  // declared carry-forward) wins; the status check is only a fallback.
  const isOpenTask = task && (task.actionable ?? ["open", "in_progress", "waiting"].includes(task.status));
  const stageExit = task?.cancel_reason_code === "stage_departed" ? task?.workflow?.stage_exit : null;

  const load = useCallback(async () => {
    if (!caseId) return;
    try {
      const { data } = await api.get(`/cases/${caseId}/workflow`);
      setView(data);
    } catch (e) { toast.error(errDetail(e, "Could not load the outcome form")); }
  }, [caseId]);

  const loadDocs = useCallback(() => {
    if (!contactId) return;
    api.get(`/documents?contact_id=${contactId}`).then((r) => setCustomerDocs(r.data || [])).catch(() => {});
  }, [contactId]);

  useEffect(() => {
    if (!open) { hydrated.current = false; setView(null); setPanel(null); setErrors({}); return; }
    idemKey.current = newKey();
    load(); loadDocs();
  }, [open, load, loadDocs]);

  // Restore the autosaved draft of THIS pending task (never a new copy).
  useEffect(() => {
    if (!task || hydrated.current) return;
    const d = task.workflow?.draft || {};
    setValues(d.values || {}); setDocs(normaliseDraftDocuments(d.documents)); setNotes(d.notes || "");
    setExtraDocs([]); setExtraTasks([]); setReminders([]);
    hydrated.current = true;
  }, [task]);

  // Debounced draft autosave — a scratch field on the task, never a completion.
  useEffect(() => {
    if (!open || !hydrated.current || !isOpenTask) return undefined;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.patch(`/cases/${caseId}/workflow/tasks/${taskId}/draft`,
        { values: { values, documents: docs, notes } })
        .then(() => setDraftState(`Draft saved ${new Date().toLocaleTimeString()}`))
        .catch(() => setDraftState("Draft not saved"));
    }, 900);
    return () => clearTimeout(saveTimer.current);
  }, [values, docs, notes, open, caseId, taskId, isOpenTask]);

  useEffect(() => {
    if (panel === "handoff" && owners.length === 0) loadAssignableOwners().then(setOwners).catch(() => {});
  }, [panel, owners.length]);

  const setVal = (k, v) => setValues((p) => ({ ...p, [k]: v }));
  const toggleDoc = (key, id) => setDocs((p) => {
    const cur = p[key] || [];
    return { ...p, [key]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });

  const upload = async (file, category, label, reqKey) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("contact_id", contactId); fd.append("case_id", caseId);
    fd.append("category", category || "Other"); fd.append("description", label || file.name);
    try {
      const { data } = await api.post("/documents/upload", fd);
      setCustomerDocs((p) => [data, ...p]);
      if (reqKey) setDocs((p) => ({ ...p, [reqKey]: [...new Set([...(p[reqKey] || []), data.document_id])] }));
      else setExtraDocs((p) => [...p, data.document_id]);
      toast.success(`Uploaded ${file.name}`);
    } catch (e) { toast.error(errDetail(e, "Upload failed")); }
  };

  // Several files in one go: each goes through the SAME existing upload
  // endpoint (auth, storage and audit unchanged) and is attached on success.
  const uploadMany = async (files, category, label, reqKey) => {
    for (const file of Array.from(files || [])) {
      // eslint-disable-next-line no-await-in-loop
      await upload(file, category, label, reqKey);
    }
  };

  const branch = useMemo(() => (schema?.branches || []).find((b) => applies(b.when, values)), [schema, values]);

  const submit = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true); setErrors({});
    const cleanValues = {};
    for (const f of schema.fields) {
      if (!applies(f.when, values)) continue;
      let v = values[f.key];
      if (f.type === "list" && typeof v === "string") v = v.split("\n").map((s) => s.trim()).filter(Boolean);
      if (f.type === "datetime" && v) v = new Date(v).toISOString();
      if (v !== undefined && v !== "") cleanValues[f.key] = v;
    }
    const cleanDocs = {};
    for (const d of schema.documents) if (applies(d.when, values) && (docs[d.key] || []).length) cleanDocs[d.key] = docs[d.key];
    try {
      const { data } = await api.post(`/cases/${caseId}/workflow/tasks/${taskId}/outcome`, {
        idempotency_key: idemKey.current, values: cleanValues, documents: cleanDocs,
        notes: notes || null, additional_document_ids: extraDocs,
        additional_tasks: extraTasks.filter((t) => t.title?.trim()).map((t) => ({
          title: t.title, due_at: t.due_at ? new Date(t.due_at).toISOString() : null })),
        reminders: reminders.filter((r) => r.remind_at).map((r) => ({
          remind_at: new Date(r.remind_at).toISOString(), note: r.note || null })),
      });
      const moved = data.transitioned_to ? ` — moved to ${data.transitioned_to}` : "";
      const gate = data.gate?.kind === "approval" ? " — sent for Leadership approval"
        : data.gate?.kind === "payment" && data.gate?.state !== "paid" ? ` — waiting for ${data.gate.label}` : "";
      toast.success(`${data.branch_label}${moved}${gate}`);
      idemKey.current = newKey();
      onSubmitted?.(data);
      onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.errors) setErrors(d.errors);
      toast.error(errDetail(e, "Outcome not recorded"));
      // The task was closed meanwhile (e.g. the Case left its stage): show
      // its real state instead of a form that can no longer be submitted.
      if (d && typeof d === "object" && d.blocker_type === "task_not_open") load();
    } finally { inFlight.current = false; setSubmitting(false); }
  };

  const post = async (path, body, ok) => {
    try {
      const { data } = await api.post(`/cases/${caseId}/workflow/tasks/${taskId}/${path}`, body);
      toast.success(ok); setPanel(null); setAux({});
      return data;
    } catch (e) { toast.error(errDetail(e)); return null; }
  };

  // R15 U1: the slot belongs to the exact (product, date) it was fetched for; changing the date invalidates it at once, late responses are ignored, and the status says why nothing can
  // be picked (closed day / not permitted / could not load) instead of a toast plus an empty list.
  const bookProduct = view?.workflow?.product_codes?.[0] || "";
  const bookPicker = useSlotPicker({
    key: aux.date ? `${bookProduct}|${aux.date}|${caseId}` : null, date: aux.date || null,
    load: async () => {
      const { data: rt } = await api.get(`/appointments/resolve-type?product_code=${bookProduct}`);
      const typeId = rt?.appointment_type?.appointment_type_id;
      if (!typeId) return [];
      return (await api.get(`/appointments/slots?appointment_type_id=${typeId}&date_str=${aux.date}`)).data;
    },
  });

  const recordConsultation = async () => {
    const appt = task?.appointment;
    if (!appt) return;
    try {
      await api.post(`/consultations/${appt.appointment_id}/outcome`, {
        outcome: aux.decision, feedback: aux.feedback || "",
        lost_reason: aux.decision === "not_interested" ? (aux.feedback || "Not interested") : null,
        closure_initiated_by: aux.decision === "not_interested" ? (aux.closure || "customer") : null,
      });
      toast.success("Consultation outcome recorded");
      onSubmitted?.({}); onOpenChange(false);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const renderField = (f) => {
    if (!applies(f.when, values)) return null;
    const id = `wf-${f.key}`;
    const err = errors[f.key];
    const common = { id, "data-testid": `wf-field-${f.key}`, "aria-invalid": !!err };
    let input;
    if (f.type === "choice") {
      input = (
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-labelledby={`${id}-label`}>
          {f.options.map((o) => (
            <Button key={o.value} type="button" size="sm" role="radio"
              aria-checked={values[f.key] === o.value}
              variant={values[f.key] === o.value ? "default" : "outline"} className="h-8 text-xs"
              data-testid={`wf-choice-${f.key}-${o.value}`} onClick={() => setVal(f.key, o.value)}>
              {o.label}
            </Button>
          ))}
        </div>);
    } else if (f.type === "longtext") {
      input = <Textarea {...common} rows={3} value={values[f.key] || ""} onChange={(e) => setVal(f.key, e.target.value)} />;
    } else if (f.type === "list") {
      input = <Textarea {...common} rows={3} placeholder="One per line"
        value={Array.isArray(values[f.key]) ? values[f.key].join("\n") : (values[f.key] || "")}
        onChange={(e) => setVal(f.key, e.target.value)} />;
    } else {
      const type = { date: "date", datetime: "datetime-local", number: "number" }[f.type] || "text";
      input = <Input {...common} type={type} className="h-9" value={values[f.key] ?? ""}
        onChange={(e) => setVal(f.key, e.target.value)} />;
    }
    return (
      <div key={f.key} className="space-y-1">
        <Label htmlFor={id} id={`${id}-label`} className="text-xs">
          {f.label}{f.required && <span className="text-[hsl(var(--danger))]"> *</span>}
        </Label>
        {input}
        {err && <p className="text-[11px] text-[hsl(var(--danger))]" role="alert">{err}</p>}
      </div>
    );
  };

  // A configurable multi-document evidence requirement (server registry:
  // DocReq.categories). Existing documents of THIS customer in an accepted
  // category are selectable (no duplicate upload); new files upload through
  // the existing endpoint under a chosen accepted category. The server
  // re-validates everything — this only guides the user.
  const renderEvidenceReq = (d) => {
    const err = errors[`doc:${d.key}`];
    const selected = docs[d.key] || [];
    const cats = Object.fromEntries(d.categories.map((c) => [c.value, c]));
    const evidenceCats = d.categories.filter((c) => c.evidence);
    const usable = customerDocs.filter((doc) => USABLE_DOC_STATUSES.includes(doc.status)
      && cats[doc.category]
      && !(d.case_scope === "case" && doc.case_id && doc.case_id !== caseId));
    const visibleIds = new Set(usable.map((x) => x.document_id));
    const stale = selected.filter((id) => !visibleIds.has(id));
    const byId = Object.fromEntries(customerDocs.map((x) => [x.document_id, x]));
    const evidenceCount = selected.filter((id) => cats[byId[id]?.category]?.evidence).length;
    const upCat = uploadCategory[d.key] || evidenceCats[0]?.value || d.categories[0]?.value;
    const need = d.required ? d.min_count : 0;
    return (
      <div key={d.key} className="space-y-2" data-testid={`wf-doc-${d.key}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium">{d.label}{d.required && <span className="text-[hsl(var(--danger))]"> *</span>}</span>
          <span className="text-[11px] text-muted-foreground" data-testid={`wf-doc-count-${d.key}`}>
            {need ? `${evidenceCount} of ${need} required evidence document${need === 1 ? "" : "s"} attached` : `${selected.length} attached`}
          </span>
        </div>
        {d.help && <p className="text-[11px] text-muted-foreground">{d.help}</p>}
        <div className="max-h-40 overflow-y-auto space-y-1" data-testid={`wf-doc-list-${d.key}`}>
          {usable.length === 0 && stale.length === 0 && (
            <p className="text-[11px] text-muted-foreground">No suitable documents on file yet — upload below.</p>)}
          {[...usable.map((doc) => doc.document_id), ...stale].map((id) => {
            const doc = byId[id];
            return (
              <label key={id} className="flex items-center gap-2 text-xs cursor-pointer">
                <Checkbox checked={selected.includes(id)} onCheckedChange={() => toggleDoc(d.key, id)}
                  data-testid={`wf-doc-${d.key}-${id}`} />
                <span className="truncate">{doc?.name || id}</span>
                <span className="text-muted-foreground">{cats[doc?.category]?.label || doc?.category || ""}</span>
                {cats[doc?.category] && !cats[doc.category].evidence && (
                  <Badge variant="outline" className="text-[9px]">supporting</Badge>)}
              </label>);
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={upCat} onValueChange={(v) => setUploadCategory((p) => ({ ...p, [d.key]: v }))}>
            <SelectTrigger className="h-8 text-xs w-64" data-testid={`wf-upload-category-${d.key}`}
              aria-label="Document type for new uploads"><SelectValue /></SelectTrigger>
            <SelectContent>{d.categories.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}{c.evidence ? "" : " (supporting)"}</SelectItem>))}</SelectContent>
          </Select>
          <label className="text-[11px] underline cursor-pointer inline-flex items-center gap-1">
            <Upload className="h-3 w-3" />Upload files
            <input type="file" multiple className="sr-only" data-testid={`wf-upload-${d.key}`}
              onChange={(e) => { uploadMany(e.target.files, upCat, null, d.key); e.target.value = ""; }} />
          </label>
        </div>
        {err && <p className="text-[11px] text-[hsl(var(--danger))]" role="alert">{err}</p>}
      </div>
    );
  };

  const renderDocReq = (d) => {
    if (!applies(d.when, values)) return null;
    if (d.categories?.length) return renderEvidenceReq(d);
    const err = errors[`doc:${d.key}`];
    const selected = docs[d.key] || [];
    return (
      <div key={d.key} className="space-y-1" data-testid={`wf-doc-${d.key}`}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium">{d.label}{d.required && <span className="text-[hsl(var(--danger))]"> *</span>}</span>
          <label className="ml-auto text-[11px] underline cursor-pointer inline-flex items-center gap-1">
            <Upload className="h-3 w-3" />Upload
            <input type="file" className="sr-only" data-testid={`wf-upload-${d.key}`}
              onChange={(e) => { upload(e.target.files?.[0], d.category, d.label, d.key); e.target.value = ""; }} />
          </label>
        </div>
        <div className="max-h-28 overflow-y-auto space-y-1">
          {customerDocs.length === 0 && <p className="text-[11px] text-muted-foreground">No documents on file — upload one.</p>}
          {customerDocs.map((doc) => (
            <label key={doc.document_id} className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox checked={selected.includes(doc.document_id)}
                onCheckedChange={() => toggleDoc(d.key, doc.document_id)}
                data-testid={`wf-doc-${d.key}-${doc.document_id}`} />
              <span className="truncate">{doc.name}</span>
              <span className="text-muted-foreground">{doc.category}</span>
            </label>
          ))}
        </div>
        {err && <p className="text-[11px] text-[hsl(var(--danger))]" role="alert">{err}</p>}
      </div>
    );
  };

  const consultation = schema?.kind === "consultation";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wf-outcome-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{schema?.title || "Outcome"}</DialogTitle>
          <DialogDescription>
            {schema ? <>Stage <b>{schema.stage}</b>{schema.mandatory ? " · mandatory" : " · optional"}
              {isOpenTask ? " — you can close this any time; the task stays pending and your draft is kept."
                : " — this task is closed; nothing can be recorded against it."}</> : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        {(view?.case?.opportunity_id || task?.opportunity_id) && (
          <WhatHappensNextPanel opportunityId={view?.case?.opportunity_id || task?.opportunity_id} compact surface="outcome-form-case" />)}
        {!schema && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>}
        {schema && !isOpenTask && stageExit && (
          <div className="text-sm space-y-1" data-testid="wf-task-stage-left">
            <p><b>No longer actionable.</b> This task belonged to <b>{stageExit.from_stage}</b>, which the
              Case left on {fmtIST(stageExit.at, { year: true })} — it moved {stageExit.direction === "back" ? "back" : "on"} to{" "}
              <b>{stageExit.to_stage}</b>{stageExit.reason ? <> ({stageExit.reason})</> : null}.</p>
            <p className="text-muted-foreground text-xs">Nothing can be recorded against it. If the Case re-enters
              {" "}{stageExit.from_stage}, a fresh task is created for that visit.</p>
          </div>
        )}
        {schema && !isOpenTask && !stageExit && (
          <p className="text-sm text-muted-foreground">
            {task && ["open", "in_progress", "waiting"].includes(task.status)
              ? <>This task belongs to <b>{task.workflow?.stage}</b>, which the Case has left — it is no longer actionable.</>
              : <>This task is already <b>{task?.status}</b>.</>}
          </p>
        )}
        {schema && isOpenTask && (
          <div className="space-y-3">
            {schema.description && <p className="text-xs text-muted-foreground">{schema.description}</p>}
            {consultation ? (
              <Section title="Admission-decision consultation">
                {task.appointment ? (
                  <div className="space-y-2">
                    <div className="text-xs">Booked for <b>{fmtIST(task.appointment.scheduled_start, { year: true })}</b>
                      {task.appointment.counsellor_name ? ` with ${task.appointment.counsellor_name}` : ""}
                      <Badge variant="outline" className="ml-2 text-[9px] capitalize">{task.appointment.status}</Badge></div>
                    <Label className="text-xs">Consultation outcome</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {[["proceed_for_admission", "Proceed for Admission"], ["not_ready", "Not Ready"],
                        ["not_interested", "Not Interested"]].map(([k, l]) => (
                        <Button key={k} size="sm" variant={aux.decision === k ? "default" : "outline"}
                          className="h-8 text-xs" data-testid={`wf-consult-${k}`}
                          onClick={() => setAux((p) => ({ ...p, decision: k }))}>{l}</Button>))}
                    </div>
                    <Label htmlFor="wf-consult-feedback" className="text-xs">Consultation note (required)</Label>
                    <Textarea id="wf-consult-feedback" rows={3} value={aux.feedback || ""}
                      onChange={(e) => setAux((p) => ({ ...p, feedback: e.target.value }))} />
                    <Button size="sm" disabled={!aux.decision || !(aux.feedback || "").trim()}
                      data-testid="wf-consult-record" onClick={recordConsultation}>Record consultation outcome</Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Book a real consultation (appears in Consultations).</p>
                    <div className="flex gap-2 items-end flex-wrap">
                      <div><Label className="text-xs" htmlFor="wf-book-date">Date</Label>
                        <Input id="wf-book-date" type="date" className="h-9 w-40" value={aux.date || ""}
                          onChange={(e) => setAux((p) => ({ ...p, date: e.target.value }))} /></div>
                      <div className="min-w-40"><Label className="text-xs">Slot</Label>
                        {bookPicker.status === "ready" ? (
                          <Select value={bookPicker.slot || ""} onValueChange={bookPicker.select}>
                            <SelectTrigger className="h-9" data-testid="wf-book-slot"><SelectValue placeholder="Pick a slot" /></SelectTrigger>
                            <SelectContent>{bookPicker.slots.map((s) => <SelectItem key={s.start} value={s.start}>{s.label}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          bookPicker.status === "idle"
                            ? <p className="text-xs text-muted-foreground">Pick a date first.</p>
                            : <SlotPickerView picker={bookPicker} idPrefix="wf-book" compact />
                        )}</div>
                      <Button size="sm" disabled={!bookPicker.slot} data-testid="wf-book-submit"
                        onClick={async () => { if (!bookPicker.slot) return; const r = await post("book-consultation", { start: bookPicker.slot }, "Consultation booked"); if (r) load(); else bookPicker.reload(); }}>
                        Book</Button>
                    </div>
                  </div>
                )}
              </Section>
            ) : (
              <>
                {schema.fields.length > 0 && <Section title="Outcome">{schema.fields.map(renderField)}</Section>}
                {schema.documents.some((d) => applies(d.when, values)) && (
                  <Section title="Documents">{schema.documents.map(renderDocReq)}</Section>)}
                {branch && (
                  <div className="text-xs rounded-md bg-muted px-3 py-2 flex items-center gap-2" data-testid="wf-branch-preview">
                    <ArrowRight className="h-3.5 w-3.5" />
                    <span><b>{branch.label}</b>
                      {branch.gate ? ` — ${branch.gate.kind === "approval" ? "needs Leadership approval" : `waits for ${branch.gate.label} to be verified paid`}` : ""}
                      {branch.transition_to && !branch.gate ? ` — Case moves to ${branch.transition_to}` : ""}
                      {branch.discontinue ? " — the Case will be discontinued" : ""}
                      {!branch.completes_task ? " — progress saved, task stays open" : ""}</span>
                  </div>)}
                {errors._branch && <p className="text-[11px] text-[hsl(var(--danger))]" role="alert">{errors._branch}</p>}
                <Section title="Also (optional)">
                  <div className="space-y-1">
                    <Label htmlFor="wf-notes" className="text-xs">Notes</Label>
                    <Textarea id="wf-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="wf-notes" />
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span>Additional documents: {extraDocs.length}</span>
                    <label className="underline cursor-pointer inline-flex items-center gap-1">
                      <Upload className="h-3 w-3" />Attach
                      <input type="file" multiple className="sr-only" data-testid="wf-upload-additional"
                        onChange={(e) => { uploadMany(e.target.files, "Other", null, null); e.target.value = ""; }} />
                    </label>
                  </div>
                  {extraTasks.map((t, i) => (
                    <div key={i} className="flex gap-2">
                      <Input className="h-8 text-xs" placeholder="Follow-up task title" value={t.title}
                        onChange={(e) => setExtraTasks((p) => p.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
                      <Input className="h-8 text-xs w-48" type="datetime-local" value={t.due_at || ""}
                        onChange={(e) => setExtraTasks((p) => p.map((x, j) => (j === i ? { ...x, due_at: e.target.value } : x)))} />
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label="Remove task"
                        onClick={() => setExtraTasks((p) => p.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>))}
                  {reminders.map((r, i) => (
                    <div key={i} className="flex gap-2">
                      <Input className="h-8 text-xs w-48" type="datetime-local" value={r.remind_at || ""}
                        onChange={(e) => setReminders((p) => p.map((x, j) => (j === i ? { ...x, remind_at: e.target.value } : x)))} />
                      <Input className="h-8 text-xs" placeholder="Reminder note" value={r.note || ""}
                        onChange={(e) => setReminders((p) => p.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))} />
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label="Remove reminder"
                        onClick={() => setReminders((p) => p.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>))}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setExtraTasks((p) => [...p, { title: "" }])}>
                      <Plus className="h-3 w-3 mr-1" />Task</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setReminders((p) => [...p, { remind_at: "" }])}>
                      <Bell className="h-3 w-3 mr-1" />Reminder</Button>
                  </div>
                </Section>
              </>
            )}

            {/* Non-completing actions */}
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="wf-remind-later" onClick={() => setPanel(panel === "remind" ? null : "remind")}>Remind me later</Button>
              {schema.actions.includes("skip") && <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="wf-skip" onClick={() => setPanel(panel === "skip" ? null : "skip")}>Skip…</Button>}
              {schema.actions.includes("handoff") && <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="wf-handoff" onClick={() => setPanel(panel === "handoff" ? null : "handoff")}>Hand off…</Button>}
              {schema.actions.includes("share") && <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="wf-share" onClick={() => setPanel(panel === "share" ? null : "share")}>Share with customer…</Button>}
            </div>
            {panel === "remind" && (
              <Section title="Remind me later (the task stays open)">
                <div className="flex gap-2">
                  <Input type="datetime-local" className="h-8 text-xs w-52" value={aux.remind_at || ""} data-testid="wf-remind-at"
                    onChange={(e) => setAux((p) => ({ ...p, remind_at: e.target.value }))} />
                  <Button size="sm" className="h-8 text-xs" disabled={!aux.remind_at} data-testid="wf-remind-save"
                    onClick={() => post("reminder", { remind_at: new Date(aux.remind_at).toISOString() }, "Reminder set")}>Set</Button>
                </div>
              </Section>)}
            {panel === "skip" && (
              <Section title="Skip this optional task">
                <Textarea rows={2} placeholder="Reason (required)" value={aux.reason || ""} data-testid="wf-skip-reason"
                  onChange={(e) => setAux((p) => ({ ...p, reason: e.target.value }))} />
                <Button size="sm" className="h-8 text-xs" disabled={!(aux.reason || "").trim()} data-testid="wf-skip-confirm"
                  onClick={async () => { const r = await post("skip", { reason: aux.reason }, "Task skipped"); if (r) { onSubmitted?.({}); onOpenChange(false); } }}>Skip task</Button>
              </Section>)}
            {panel === "handoff" && (
              <Section title="Hand this task to another employee">
                <Select value={aux.owner || ""} onValueChange={(v) => setAux((p) => ({ ...p, owner: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="wf-handoff-owner"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
                  <SelectContent>{owners.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
                </Select>
                <Input className="h-8 text-xs" placeholder="Reason (optional)" value={aux.reason || ""}
                  onChange={(e) => setAux((p) => ({ ...p, reason: e.target.value }))} />
                <Button size="sm" className="h-8 text-xs" disabled={!aux.owner}
                  onClick={async () => { const r = await post("handoff", { owner_user_id: aux.owner, reason: aux.reason || null }, "Task handed off"); if (r) load(); }}>Hand off</Button>
              </Section>)}
            {panel === "share" && (
              <Section title="Share details with the customer">
                <Select value={aux.channel || "whatsapp"} onValueChange={(v) => setAux((p) => ({ ...p, channel: v }))}>
                  <SelectTrigger className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="whatsapp">WhatsApp</SelectItem><SelectItem value="email">Email</SelectItem></SelectContent>
                </Select>
                <Textarea rows={3} placeholder="Message" value={aux.message || ""} data-testid="wf-share-message"
                  onChange={(e) => setAux((p) => ({ ...p, message: e.target.value }))} />
                <Button size="sm" className="h-8 text-xs" disabled={!(aux.message || "").trim()}
                  onClick={async () => {
                    const r = await post("share", { channel: aux.channel || "whatsapp", message: aux.message },
                      (aux.channel || "whatsapp") === "whatsapp" ? "WhatsApp queued" : "Email draft ready");
                    if (r?.gmail_url) window.open(r.gmail_url, "_blank", "noopener");
                  }}>Send</Button>
              </Section>)}
          </div>
        )}
        <DialogFooter className="items-center gap-2">
          <span className="text-[11px] text-muted-foreground mr-auto" aria-live="polite">{draftState}</span>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="wf-close">Close</Button>
          {schema && isOpenTask && !consultation && (
            <Button onClick={submit} disabled={submitting} data-testid="wf-submit">
              {submitting ? "Submitting…" : "Submit outcome"}
            </Button>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
